import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {type AgregadoCuestionario} from '@/hooks/bateria/use-bateria'
import {dimensionesEnRojo, dominantNivel, enRiesgoPct, NIVEL_TONE} from '@/lib/bateria'

type KpiTone = 'red' | 'orange' | 'amber' | 'lime' | 'green' | 'neutral'

// The rail has four urgency tiers: lime folds into green.
const RAIL: Record<KpiTone, string> = {
  red: 'kpi-rail-red',
  orange: 'kpi-rail-orange',
  amber: 'kpi-rail-amber',
  lime: 'kpi-rail-green',
  green: 'kpi-rail-green',
  neutral: '',
}

function KpiCard({
  label,
  value,
  sub,
  tone,
  hero,
  testId,
}: {
  label: string
  value: React.ReactNode
  sub?: string
  tone?: KpiTone
  hero?: boolean
  testId: string
}) {
  return (
    <div
      className={`surface surface-raised relative flex flex-col gap-1.5 py-4 pr-4 pl-5 ${tone ? RAIL[tone] : ''}`}
      data-testid={testId}>
      <p className="font-mono text-[10px] font-medium tracking-[0.14em] text-muted-foreground uppercase">{label}</p>
      <div className="flex items-baseline gap-2">
        {hero && tone ? (
          <span className={`font-heading text-4xl leading-none ${heroValueClass(tone)}`}>{value}</span>
        ) : (
          <span className="font-heading text-2xl leading-none text-foreground">{value}</span>
        )}
      </div>
      {sub && <p className="text-xs leading-snug text-muted-foreground">{sub}</p>}
    </div>
  )
}

function heroValueClass(tone: KpiTone): string {
  if (tone === 'green' || tone === 'lime') return 'text-primary'
  if (tone === 'amber') return 'text-tone-amber-fg'
  if (tone === 'orange') return 'text-tone-orange-fg'
  return 'text-tone-red-fg'
}

/** Always read off the consolidado: the population-wide picture. */
export function ResultadosKpis({
  consolidado,
  validados,
  totalPaquetes,
}: {
  consolidado: AgregadoCuestionario
  validados: number
  totalPaquetes: number
}) {
  const {t} = useTranslation()
  const total = consolidado.total_general
  const nivelGeneral = dominantNivel(total)
  const riesgoPct = enRiesgoPct(total)
  const rojas = dimensionesEnRojo(consolidado)
  const participacionPct = totalPaquetes > 0 ? Math.round((validados / totalPaquetes) * 100) : 0

  const riesgoTone = riesgoPct >= 40 ? 'red' : riesgoPct >= 20 ? 'orange' : riesgoPct >= 10 ? 'amber' : 'green'

  const nivelRailBy: Record<string, KpiTone> = {
    sin_riesgo: 'green',
    bajo: 'green',
    medio: 'amber',
    alto: 'orange',
    muy_alto: 'red',
  }
  const nivelRail: KpiTone = nivelGeneral ? (nivelRailBy[nivelGeneral] ?? 'neutral') : 'neutral'

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="resultados-kpis">
      <KpiCard
        testId="kpi-participacion"
        label={t('admin.bateria.resultados.kpiParticipacion')}
        value={t('admin.bateria.resultados.kpiParticipacionValue', {
          validados,
          total: totalPaquetes,
        })}
        sub={t('admin.bateria.resultados.kpiParticipacionPct', {pct: participacionPct})}
      />
      <KpiCard
        testId="kpi-en-riesgo"
        label={t('admin.bateria.resultados.kpiEnRiesgo')}
        value={`${riesgoPct}%`}
        tone={riesgoTone}
        hero
        sub={t('admin.bateria.resultados.kpiEnRiesgoSub')}
      />
      <KpiCard
        testId="kpi-nivel-general"
        label={t('admin.bateria.resultados.kpiNivelGeneral')}
        tone={nivelRail}
        value={
          nivelGeneral ? (
            <Tag tone={NIVEL_TONE[nivelGeneral]} className="text-sm">
              {t(`admin.bateria.nivel.${nivelGeneral}`)}
            </Tag>
          ) : (
            <Tag tone="neutral" className="text-sm">
              {t('admin.bateria.nivel.sinDato')}
            </Tag>
          )
        }
      />
      <KpiCard
        testId="kpi-prioridades"
        label={t('admin.bateria.resultados.kpiPrioridades')}
        value={rojas}
        tone={rojas > 0 ? 'red' : 'green'}
        sub={t('admin.bateria.resultados.kpiPrioridadesSub', {count: rojas})}
      />
    </div>
  )
}
